import { useEffect, useState } from 'react';
import {
  ConnectionState,
  ConnectorStatus,
  type ConnectorId,
  type StopReason,
  type VehicleProfileId,
} from '@/state/types';
import { statusOptionsFor, stopReasonsForVersion } from '@/state/types';
import { useStationStore } from '@/state/useStationStore';
import { useCostStore } from '@/state/useCostStore';
import { useUIStore } from '@/state/useUIStore';
import { useVehicleStore } from '@/state/useVehicleStore';
import { ConnectorLED } from './ConnectorLED';

/**
 * NIT-012 — colour-coded connector status. Each status maps to a
 * Tailwind tone matching evse2's classMap.
 */
const STATUS_TONE: Record<ConnectorStatus, string> = {
  [ConnectorStatus.Available]: 'bg-primary-base/80 text-primary-on',
  [ConnectorStatus.Preparing]: 'bg-sky-700/80 text-sky-50',
  [ConnectorStatus.Charging]: 'bg-blue-700/80 text-blue-50',
  [ConnectorStatus.SuspendedEV]: 'bg-amber-700/80 text-amber-50',
  [ConnectorStatus.SuspendedEVSE]: 'bg-amber-800/80 text-amber-50',
  [ConnectorStatus.Finishing]: 'bg-slate-600/80 text-slate-100',
  [ConnectorStatus.Reserved]: 'bg-indigo-700/80 text-indigo-50',
  [ConnectorStatus.Unavailable]: 'bg-slate-700/80 text-slate-300',
  [ConnectorStatus.Faulted]: 'bg-rose-700/80 text-rose-50',
};

export function ConnectorRow({ connectorId }: { connectorId: ConnectorId }): JSX.Element {
  const connector = useStationStore((s) => s.connectors.get(connectorId));
  // Session 23 — running cost from an OCPP 2.0.1 CostUpdated, if any.
  const txCost = useCostStore((s) => {
    const txId = connector?.activeTransactionId;
    return txId !== null && txId !== undefined ? s.costByTx.get(Number(txId)) : undefined;
  });
  const start = useStationStore((s) => s.startTransaction);
  const stop = useStationStore((s) => s.stopTransaction);
  const setConnectorStatus = useStationStore((s) => s.setConnectorStatus);
  const assignVehicle = useStationStore((s) => s.assignVehicle);
  const unassignVehicle = useStationStore((s) => s.unassignVehicle);
  const connectionState = useStationStore((s) => s.connectionState);
  const ocppVersion = useStationStore((s) => s.ocppVersion);
  const idTag = useUIStore((s) => s.authorizeInput);
  const vehicles = useVehicleStore((s) => s.profiles);

  // Stop-reason picker — the bare Stop button stops with the default
  // (Local); the ▾ split opens a version-aware menu of every reason the
  // station's OCPP version may legally send.
  const [reasonMenuOpen, setReasonMenuOpen] = useState(false);
  const stopReasons = stopReasonsForVersion(ocppVersion);
  useEffect(() => {
    if (!reasonMenuOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setReasonMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [reasonMenuOpen]);

  if (!connector) return <></>;
  const isActive = connector.activeTransactionId !== null;
  const isConnected = connectionState === ConnectionState.Connected;
  // NIT-007 — every CALL-issuing button requires a live transport. The
  // status dropdown also fires StatusNotification, so gate it the same way.
  const canStart = isConnected && !isActive && connector.status !== ConnectorStatus.Faulted;
  const canStop = isConnected && isActive;
  const disabledTitle = isConnected ? undefined : 'Connect first';

  // BUG-007 + NIT-018 — Restructured layout:
  //   LEFT meta cluster  = all CONFIGURATION affordances (status pill,
  //                        status select, vehicle select, SoC pill) in
  //                        a 3-row vertical stack under "Connector N".
  //   RIGHT action zone  = only RUNTIME state (idle/tx text + Start/Stop).
  // `min-w-0` on both flex children lets text truncate instead of
  // overflowing into the right column (BUG-007); `shrink-0` on action
  // zone keeps Start/Stop pinned to the right edge.
  return (
    <li
      className="flex flex-col gap-3 rounded bg-slate-800 px-3 py-2 sm:flex-row sm:items-start sm:justify-between"
      data-testid={`connector-${connectorId}`}
    >
      <div className="flex min-w-0 items-start gap-3">
        <ConnectorLED status={connector.status} />
        <div className="min-w-0 text-sm">
          <div className="font-medium text-slate-100">Connector {connectorId}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-400">
            {/* UX-C — the badge is the read-only current state (colour-coded);
                the select below is the CONTROL. The muted "set" label keeps the
                two from reading as the same value shown twice. */}
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${STATUS_TONE[connector.status]}`}
              data-testid={`connector-${connectorId}-status`}
              title="Current connector status"
            >
              {connector.status}
            </span>
            <span aria-hidden="true" className="text-[10px] uppercase tracking-wide text-slate-500">
              set
            </span>
            {/* Dropdown stays enabled even when disconnected — the action
                only patches local state in that case, so testers can pre-stage
                a connector status before clicking Connect. */}
            <select
              value={connector.status}
              onChange={(e) => {
                const next = e.target.value as ConnectorStatus;
                if (next !== connector.status) setConnectorStatus(connectorId, next);
              }}
              className="rounded border border-slate-700 bg-slate-950 px-1 py-0.5 text-[11px] text-slate-200 focus:outline-none focus:ring-1 focus:ring-primary-base"
              data-testid={`connector-${connectorId}-status-select`}
              aria-label={`Set connector ${connectorId} status`}
            >
              {statusOptionsFor(connector.status).map((opt) => (
                <option
                  key={opt.value}
                  value={opt.value}
                  disabled={!opt.allowed && !opt.current}
                  title={
                    !opt.allowed && !opt.current
                      ? `Illegal transition from ${connector.status} per OCPP 1.6J §4.2 table 3`
                      : undefined
                  }
                >
                  {opt.value}
                  {!opt.allowed && !opt.current ? ' — illegal' : ''}
                </option>
              ))}
            </select>
          </div>
          {/* NIT-018 — vehicle binding row, separate from status row. */}
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-400">
            {/* FEAT-011 — vehicle binding (always editable, even when active
                so QA can swap vehicles mid-session). SoC pill renders only
                when bound and currentSocPct is non-null (DC only). */}
            <select
              value={connector.assignedVehicleId ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  void unassignVehicle(connectorId);
                  return;
                }
                const next = vehicles.find((v) => v.id === (raw as VehicleProfileId));
                if (next) void assignVehicle(connectorId, next);
              }}
              className="rounded border border-slate-700 bg-slate-950 px-1 py-0.5 text-[11px] text-slate-200 focus:outline-none focus:ring-1 focus:ring-primary-base"
              data-testid={`connector-${connectorId}-vehicle-select`}
              aria-label={`Connector ${connectorId} vehicle`}
            >
              <option value="">— no vehicle —</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
            {connector.currentSocPct !== null && connector.assignedVehicleId !== null ? (
              <span
                className="rounded bg-slate-700 px-1.5 py-0.5 text-[10px] font-medium text-slate-100"
                data-testid={`connector-${connectorId}-soc`}
                title="State of charge"
              >
                {connector.currentSocPct.toFixed(1)}%
              </span>
            ) : null}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2 text-xs text-slate-300">
        {isActive ? (
          <span
            data-testid={`connector-${connectorId}-tx`}
            className="max-w-[20ch] truncate"
            title={`tx#${String(connector.activeTransactionId)} · ${connector.activeIdTag ?? ''}`}
          >
            tx#{String(connector.activeTransactionId)} · {connector.activeIdTag}
            {txCost !== undefined ? ` · ${txCost.toFixed(2)}` : ''}
          </span>
        ) : (
          // UX-D — capitalised to match the OCPP status labels (Available, …).
          <span className="text-slate-500">Idle</span>
        )}
        <button
          type="button"
          disabled={!canStart}
          title={disabledTitle}
          onClick={() => {
            void start(connectorId, idTag);
          }}
          className="rounded bg-primary-base px-2 py-1 text-xs text-primary-on hover:bg-primary-base disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-primary-base"
          data-testid={`start-${connectorId}`}
        >
          Start
        </button>
        <div className="relative flex">
          {/* Bare Stop → default reason (Local). */}
          <button
            type="button"
            disabled={!canStop}
            title={disabledTitle}
            onClick={() => {
              setReasonMenuOpen(false);
              void stop(connectorId);
            }}
            className="rounded-l bg-rose-700 px-2 py-1 text-xs text-rose-50 hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-rose-300"
            data-testid={`stop-${connectorId}`}
          >
            Stop
          </button>
          {/* ▾ → version-aware reason menu. */}
          <button
            type="button"
            disabled={!canStop}
            title={isConnected ? 'Stop with a specific reason' : disabledTitle}
            aria-label="Choose stop reason"
            aria-haspopup="menu"
            aria-expanded={reasonMenuOpen}
            onClick={() => setReasonMenuOpen((o) => !o)}
            className="rounded-r border-l border-rose-900/60 bg-rose-700 px-1 py-1 text-xs text-rose-50 hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-rose-300"
            data-testid={`stop-reason-toggle-${connectorId}`}
          >
            <span aria-hidden="true">▾</span>
          </button>
          {reasonMenuOpen ? (
            <>
              {/* Backdrop — closes the menu on any outside click. */}
              <button
                type="button"
                aria-hidden="true"
                tabIndex={-1}
                className="fixed inset-0 z-10 cursor-default"
                onClick={() => setReasonMenuOpen(false)}
              />
              <ul
                role="menu"
                aria-label={`Stop reason for connector ${connectorId}`}
                className="absolute right-0 top-full z-20 mt-1 max-h-64 w-48 overflow-auto rounded border border-slate-600 bg-slate-900 py-1 shadow-lg"
                data-testid={`stop-reason-menu-${connectorId}`}
              >
                {stopReasons.map((r: StopReason) => (
                  <li key={r} role="none">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setReasonMenuOpen(false);
                        void stop(connectorId, r);
                      }}
                      className="block w-full px-3 py-1 text-left text-xs text-slate-200 hover:bg-rose-700 hover:text-rose-50 focus:bg-rose-700 focus:text-rose-50 focus:outline-none"
                      data-testid={`stop-reason-${connectorId}-${r}`}
                    >
                      {r}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      </div>
    </li>
  );
}
